// Klientens indstillinger (firma, booking, moms, beskeder, frister, medlemskab, gebyrer, betaling). Gemmes som én JSON pr. klient;
// felter, der mangler (fx efter en opdatering af Freemi), udfyldes fra standarden, så gamle klienter aldrig går i stykker.
import { STANDARD_FRISTER, type Frister } from './kundecenter.ts';
import { STANDARD_MEDLEM, type MedlemsIndst } from './medlemskab.ts';
import { STANDARD_GEBYRER, type GebyrIndst } from './gebyrer.ts';
import type { BookingIndst } from './motor.ts';
import { STANDARD_RUTE_FLYT } from './ruteoptimering.ts';
import { STANDARD_LIVE, type LiveIndst } from './liveBeskeder.ts';
import { STANDARD_BETALING, type BetalingIndst } from './betaling.ts';

export type Indstillinger = {
  firma: { navn: string; telefon: string; email: string; adresse: string; cvr: string; hjemmeside: string; handelsbetingelser?: string; privatliv?: string };
  booking: BookingIndst;
  kunder: { login: boolean; emailKraevet?: boolean; byKraevet?: boolean };   // standardfelter på bookingen: er e-mail og by påkrævet? (standard: ja)
  moms: { procent: number; visPriser: 'ekskl' | 'inkl' };
  trustpilot: { vis: boolean; score: number; antal: number; url: string; udtalelse: { tekst: string; navn: string } };
  beskeder: { smsAfsender: string; mailAfsender: string; mailFra?: string; stilleFra: number; stilleTil: number };   // mailFra: afsenderadresse på hoveddomænet (afsender.ts)
  frister: Frister;
  medlemskab: MedlemsIndst;
  gebyrer: GebyrIndst;
  standardUge: { ugedag: number; start: number; slut: number }[];
  steamer: SteamerIndst;
  liveBeskeder: LiveIndst;   // live-beskeder på bookingsiden (src/core/liveBeskeder.ts)
  betaling: BetalingIndst;   // MobilePay-nummer, Viva-link og telefontid i betalingsmailen (src/core/betaling.ts)
};

/** Ude hos kunden: skal ekstra tilvalg, steameren tilføjer på stedet, også sendes til kunden på SMS (med link til godkendelse),
 *  eller er kundens underskrift ved afslutningen nok? */
export type SteamerIndst = {
  ekstraPaaStedet: 'underskrift' | 'sms';
  /** Steamerne får også en mail, når deres plan ændres (flyttede opgaver samles i én besked). Push får de altid. Standard: til. */
  flytMail?: boolean;
};
export const STANDARD_STEAMER: SteamerIndst = { ekstraPaaStedet: 'sms' };

/** Standard for en ny virksomhed. */
export function standardIndstillinger(firma: Partial<Indstillinger['firma']> = {}): Indstillinger {
  const navn = firma.navn ?? '';
  return {
    firma: { navn, telefon: '', email: '', adresse: '', cvr: '', hjemmeside: '', handelsbetingelser: '', ...firma },
    booking: { godkendelse: 'manuel', minVarselTimer: 12, aflysFristTimer: 48, trin: 30, pause: 30, maxKoersel: 90, tidsmodel: 'fleksibel', fasteTider: [480, 660, 840], slotLaengde: 180, bufferFoer: 0, bufferEfter: 0, maxOpgaver: null, ruteFlyt: { ...STANDARD_RUTE_FLYT } },
    kunder: { login: true },
    moms: { procent: 25, visPriser: 'inkl' },   // priser inkl. moms over for forbrugere (kan rettes under Moms og priser)
    trustpilot: { vis: false, score: 0, antal: 0, url: '', udtalelse: { tekst: '', navn: '' } },
    beskeder: { smsAfsender: navn.replace(/[^A-Za-z0-9 ]/g, '').slice(0, 11) || 'Freemi', mailAfsender: navn, mailFra: '', stilleFra: 21, stilleTil: 8 },
    frister: { ...STANDARD_FRISTER },
    medlemskab: structuredClone(STANDARD_MEDLEM),
    gebyrer: structuredClone(STANDARD_GEBYRER),
    standardUge: [1, 2, 3, 4, 5].map((ugedag) => ({ ugedag, start: 480, slut: 1020 })),
    steamer: { ...STANDARD_STEAMER },
    liveBeskeder: { ...STANDARD_LIVE },
    betaling: { ...STANDARD_BETALING },
  };
}

/** Gemte indstillinger lagt oven på standarden (et niveau dybt), så manglende felter altid har en værdi. */
export function fletIndstillinger(gemt: unknown, standard: Indstillinger = standardIndstillinger()): Indstillinger {
  const g = (gemt && typeof gemt === 'object' ? gemt : {}) as Record<string, unknown>;
  const ud: Record<string, unknown> = { ...standard };
  for (const [k, v] of Object.entries(standard)) {
    const x = g[k];
    if (x === undefined) continue;
    ud[k] = v && typeof v === 'object' && !Array.isArray(v) && x && typeof x === 'object' && !Array.isArray(x) ? { ...v, ...x } : x;
  }
  return ud as Indstillinger;
}
